export type GraphemeTaiwanUsageClassification = "common" | "less-than-common" | "rarely-used";

/** Per-grapheme Taiwan MOE chart classification keyed by traditional glyph. */
export type GraphemeTaiwanUsageMap = Readonly<Record<string, GraphemeTaiwanUsageClassification>>;

/**
 * Traditional glyphs that carry a TBCL (臺灣華語文能力基準) level — i.e. that appear on the
 * curriculum's word/character list. Membership is what separates a mainstream 常用字 from one that
 * is merely chart-listed (see {@link taiwanUsageClassificationBand}).
 */
export type GraphemeTaiwanTbclSet = ReadonlySet<string>;

/** Stable empty map handed back as the fallback in contexts without the bundled asset. Frozen so it can't be mutated. */
export const EMPTY_TAIWAN_USAGE_MAP: GraphemeTaiwanUsageMap = Object.freeze({});

/** Stable empty TBCL set for the same fallback contexts as {@link EMPTY_TAIWAN_USAGE_MAP}. */
export const EMPTY_TAIWAN_TBCL_SET: GraphemeTaiwanTbclSet = Object.freeze(new Set<string>());

/**
 * Rarity band a Taiwan MOE usage classification maps to, shared by every consumer that buckets a
 * traditional glyph into `common` / `uncommon` / `rare` / `obscure`. The single source of truth for
 * "how rare is a traditional character" so grapheme-prevalence (card UI), compoundsCount
 * (grapheme-file generation) and the linktree groups can never drift.
 *
 * The four bands mirror the PRC 通用规范汉字表 tiers (1 → common, 2 → uncommon, 3 → rare, unlisted →
 * obscure). On the traditional side the TBCL (臺灣華語文能力基準) curriculum supplies the top band:
 * any glyph carrying a TBCL level is `common` regardless of its chart classification. Below that the
 * three MOE charts map one-to-one — a 常用字 ("common") with no TBCL level is `uncommon`, a 次常用字
 * ("less-than-common") is `rare`, and a 罕用字 ("rarely-used") or a glyph absent from every chart is
 * `obscure`.
 */
export type TaiwanUsageBand = "common" | "uncommon" | "rare" | "obscure";

export function taiwanUsageClassificationBand(
  classification: GraphemeTaiwanUsageClassification | undefined,
  hasTbclLevel: boolean,
): TaiwanUsageBand {
  if (hasTbclLevel) {
    return "common";
  }
  if (classification === "common") {
    return "uncommon";
  }
  if (classification === "less-than-common") {
    return "rare";
  }
  return "obscure";
}
